/**
 * httpErrors.ts — pure, import-safe builders for structured 429 responses.
 *
 * All three 429 sources (rate limit, auth lockout, session cap) return a
 * JSON-RPC envelope so the consuming MCP client can tell them apart.
 *
 * Rule — id mirrors the JSON-RPC request id when known
 * ---------------------------------------------------------------------------
 * JSON-RPC requires the response id to mirror the request id so the client can
 * correlate. All three call-sites run AFTER the global `express.json()`
 * middleware, so a POST body is already parsed and callers SHOULD thread
 * `req.body?.id` through `BuildParams.id` (the POST `/mcp` rate-limit,
 * auth-lockout, and session-cap gates all do). Requests with no parsable body
 * (GET/DELETE, or a malformed POST) have no id to mirror — those omit `id` and
 * it defaults to `null`, which is the correct JSON-RPC value for "unknown id".
 * ---------------------------------------------------------------------------
 */

export interface Built429 {
  status: 429;
  headers: Record<string, string>;
  body: unknown;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Clamp to non-negative, round to integer — used for both the Retry-After
 * header value and the `retryAfterSeconds` field in `data`.
 */
function clampSeconds(s: number): number {
  return Math.max(0, Math.round(s));
}

type Reason = "rate_limited" | "auth_lockout" | "session_cap";

/** JSON-RPC request/response id. `null` is the spec value for "unknown id". */
type JsonRpcId = string | number | null;

interface BuildParams {
  retryAfterSeconds: number;
  limit: number;
  current: number;
  /** Request id to mirror; defaults to null when the caller has no body id. */
  id?: JsonRpcId;
}

function build(
  reason: Reason,
  message: string,
  p: BuildParams,
): Built429 {
  const seconds = clampSeconds(p.retryAfterSeconds);
  return {
    status: 429,
    headers: { "Retry-After": String(seconds) },
    body: {
      jsonrpc: "2.0",
      id: p.id ?? null,
      error: {
        code: -32000,
        message,
        data: {
          reason,
          retryAfterSeconds: seconds,
          limit: p.limit,
          current: p.current,
        },
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Public builders
// ---------------------------------------------------------------------------

/**
 * Per-IP rate-limit 429 (source: mcpLimiter middleware).
 * reason = "rate_limited"
 */
export function buildRateLimitError(p: BuildParams): Built429 {
  const seconds = clampSeconds(p.retryAfterSeconds);
  return build(
    "rate_limited",
    `Too many requests — retry after ${seconds} s.`,
    p,
  );
}

/**
 * Per-IP auth-failure lockout 429 (source: checkAuthBlock).
 * reason = "auth_lockout"
 */
export function buildAuthLockoutError(p: BuildParams): Built429 {
  const seconds = clampSeconds(p.retryAfterSeconds);
  return build(
    "auth_lockout",
    `Too many failed auth attempts — locked out, retry after ${seconds} s.`,
    p,
  );
}

/**
 * Per-key session-cap 429 (source: planKeyAdmission !admit fallback).
 * reason = "session_cap"
 *
 * @param p.poll - true when ALL blockers have inFlight > 0 (no grace deadline
 *   can be predicted; the 5 s value is a fixed fallback, not a guarantee).
 *   The message reads as a poll-hint rather than a hard deadline.
 *   false (default) when at least one blocker is grace-protected; the
 *   retryAfterSeconds is a real guarantee and the message names it.
 */
export function buildSessionCapError(
  p: BuildParams & { poll?: boolean },
): Built429 {
  const seconds = clampSeconds(p.retryAfterSeconds);
  const message = p.poll
    ? "Server busy — all sessions active; retry shortly."
    : `Too many active sessions for this key — retry after ${seconds} s.`;
  return build("session_cap", message, p);
}

// ---------------------------------------------------------------------------
// Sender
// ---------------------------------------------------------------------------

/**
 * Minimal structural shape of the response object send429 needs. Any
 * Express/Hono response satisfies it; the explicit interface (rather than
 * `any`) keeps a wrong argument from compiling and silently swallowing a 429.
 */
export interface Http429Response {
  headersSent: boolean;
  setHeader(name: string, value: string): unknown;
  status(code: number): { json(body: unknown): unknown };
}

/**
 * Write a Built429 to an Express/Hono-compatible response object.
 *
 * Guards `res.headersSent` first so calling this on an already-flushed
 * response is a safe no-op (e.g. when a middleware has already sent a body).
 */
export function send429(res: Http429Response, built: Built429): void {
  if (res.headersSent) return;
  for (const [k, v] of Object.entries(built.headers)) {
    res.setHeader(k, v);
  }
  res.status(built.status).json(built.body);
}
